<main class="grow" role="content">
  <div class="kt-container-fixed bonds-wide-container py-5 lg:py-7.5">
    <div class="kt-card">
      <div class="kt-card-header min-h-16 flex-wrap gap-3">
        <h1 class="kt-card-title">{{ listingLabel() }}</h1>

        <div class="inline-flex items-center gap-2.5 flex-wrap">
          <input
            #searchInput
            type="text"
            class="kt-input sm:w-72"
            [placeholder]="'Search ' + listingLabelLowercase() + '...'"
            [value]="searchTerm()"
            (input)="onSearchInput(searchInput.value)"
            [attr.aria-label]="'Search ' + listingLabelLowercase()"
          />

          <button
            type="button"
            class="kt-btn kt-btn-outline"
            (click)="onRetry()"
            [disabled]="isLoading()"
          >
            <i class="ki-filled ki-arrows-circle text-sm" aria-hidden="true"></i>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div class="kt-card-content p-0">
        @if (isLoading()) {
          <div
            class="catalog-loading"
            role="status"
            aria-live="polite"
            [attr.aria-label]="'Loading ' + listingLabelLowercase()"
          >
            <span class="catalog-loading-spinner" aria-hidden="true"></span>
            <span class="catalog-loading-text">Loading...</span>
          </div>
        }

        @if (errorMessage()) {
          <div class="p-5 lg:p-7.5">
            <div class="kt-alert kt-alert-destructive flex items-center justify-between gap-3">
              <span>{{ errorMessage() }}</span>
              <button type="button" class="kt-btn kt-btn-sm kt-btn-light" (click)="onRetry()">Retry</button>
            </div>
          </div>
        }

        @if (!errorMessage() && !isLoading()) {
          <div class="kt-card-table bonds-compact-table">
            <div class="kt-table-wrapper kt-scrollable">
              <table class="kt-table">
                <thead>
                  <tr>
                    @for (column of columns; track column.key) {
                      <th
                        scope="col"
                        [class.text-end]="column.alignEnd"
                        [class.bonds-id-header]="column.key === 'dealid' || column.key === 'tradeid' || column.key === 'structureid' || column.key === 'securityid'"
                      >
                        <button
                          type="button"
                          class="bonds-table-col-btn"
                          [class.is-active]="sortBy() === column.key"
                          [class.justify-end]="column.alignEnd"
                          [class.bonds-id-col-btn]="column.key === 'dealid' || column.key === 'tradeid' || column.key === 'structureid' || column.key === 'securityid'"
                          (click)="onSort(column.key)"
                        >
                          <span>{{ column.label }}</span>
                          <span class="bonds-sort-indicator">{{ getSortIndicator(column.key) }}</span>
                        </button>
                      </th>
                    }

                  </tr>
                </thead>

                <tbody>
                  @if (!isLoading() && rows().length === 0) {
                    <tr>
                      <td colspan="14" class="text-center text-secondary-foreground py-6">
                        No {{ listingLabelLowercase() }} found.
                      </td>
                    </tr>
                  } @else {
                    @for (item of rows(); track trackByRow($index, item)) {
                      <tr>
                        <td class="bonds-id-cell">{{ item.dealId || '-' }}</td>
                        <td class="bonds-id-cell">
                          @if (item.tradeId) {
                            <a
                              class="text-primary hover:underline"
                              [routerLink]="['/bond-detail', item.tradeId]"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {{ item.tradeId }}
                            </a>
                          } @else {
                            -
                          }
                        </td>
                        <td class="bonds-id-cell">{{ item.structureId || '-' }}</td>
                        <td class="bonds-id-cell bonds-security-id-cell">
                          @if (item.securityId) {
                            <a
                              class="text-primary hover:underline"
                              [routerLink]="['/bond-definition', item.securityId]"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {{ item.securityId }}
                            </a>
                          } @else {
                            -
                          }
                        </td>
                        <td class="bonds-id-cell">{{ item.cusip || '-' }}</td>
                        <td class="bonds-id-cell">{{ item.isin || '-' }}</td>
                        <td>{{ item.securityType || '-' }}</td>
                        <td>{{ formatDate(item.tradeDate) }}</td>
                        <td>{{ formatDate(item.settlementDate) }}</td>
                        <td>{{ formatDate(item.maturityDate) }}</td>
                        <td>{{ item.currency || '-' }}</td>
                        <td class="text-end">{{ formatNumber(item.notional, 2, true) }}</td>
                        <td class="text-end">{{ formatNumber(item.coupon, 3) }}</td>
                        <td>{{ item.marketType || '-' }}</td>
                      </tr>
                    }
                  }
                </tbody>
              </table>
            </div>

            <div class="flex items-center justify-between gap-4 flex-wrap px-5 py-4">
              <div class="kt-datatable-info text-sm text-secondary-foreground">
                @if (totalRecords() === 0) {
                  No records
                } @else {
                  Showing {{ startRecord() }} to {{ endRecord() }} of {{ totalRecords() }} entries
                }
              </div>

              <div class="inline-flex items-center gap-3 flex-wrap">
                <label class="inline-flex items-center gap-2 text-sm text-secondary-foreground">
                  <span>Show</span>
                  <select
                    #pageSizeSelect
                    class="kt-select kt-select-sm w-20"
                    [value]="pageSize()"
                    (change)="onPageSizeChange(pageSizeSelect.value)"
                  >
                    @for (value of pageSizeOptions; track value) {
                      <option [value]="value" [selected]="value === pageSize()">{{ value }}</option>
                    }
                  </select>
                </label>

                <div class="inline-flex items-center gap-1">
                  <button
                    type="button"
                    class="kt-btn kt-btn-sm kt-btn-outline"
                    (click)="goToFirstPage()"
                    [disabled]="isLoading() || isFirstPage()"
                  >
                    First
                  </button>

                  <button
                    type="button"
                    class="kt-btn kt-btn-sm kt-btn-outline"
                    (click)="goToPreviousPage()"
                    [disabled]="isLoading() || isFirstPage()"
                  >
                    Previous
                  </button>

                  @for (page of visiblePages(); track page) {
                    <button
                      type="button"
                      [class]="page === pageNumber() ? 'kt-btn kt-btn-sm kt-btn-primary' : 'kt-btn kt-btn-sm kt-btn-outline'"
                      (click)="goToPage(page)"
                      [disabled]="isLoading()"
                    >
                      {{ page }}
                    </button>
                  }

                  <button
                    type="button"
                    class="kt-btn kt-btn-sm kt-btn-outline"
                    (click)="goToNextPage()"
                    [disabled]="isLoading() || isLastPage()"
                  >
                    Next
                  </button>

                  <button
                    type="button"
                    class="kt-btn kt-btn-sm kt-btn-outline"
                    (click)="goToLastPage()"
                    [disabled]="isLoading() || isLastPage()"
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</main>